import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BLOG_LOCALE_PREFIXES, blogIndexPath, blogArticlePath } from '../../data/blog/localePrefixes';
import { blogManifestLoaders } from '../../data/blog/manifestLoaders.generated';
import './style.css';

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'ru', label: 'RU' },
  { code: 'fi', label: 'FI' },
];

// pathname -> { locale, slug: string | null } if it's a blog route,
// otherwise null. slug is null on an index route.
function matchBlogRoute(pathname) {
  for (const locale of Object.keys(BLOG_LOCALE_PREFIXES)) {
    const indexPath = blogIndexPath(locale);
    if (pathname === indexPath) return { locale, slug: null };
    if (pathname.startsWith(`${indexPath}/`)) {
      return { locale, slug: pathname.slice(indexPath.length + 1) };
    }
  }
  return null;
}

const LangSwitcher = () => {
  const { i18n } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const current = i18n.language?.slice(0, 2) || 'en';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectionRef = useRef(0);

  const currentLang = LANGS.find(l => l.code === current) || LANGS[0];
  const others = LANGS.filter(l => l.code !== current);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // On a blog route the URL itself encodes the language (design doc
  // §6.2) -- switching there must navigate, not just call
  // changeLanguage() in place like everywhere else on the site.
  const handleSelect = async (code) => {
    setOpen(false);
    const mySelection = ++selectionRef.current;
    const blogMatch = matchBlogRoute(location.pathname);

    if (!blogMatch) {
      i18n.changeLanguage(code);
      return;
    }

    if (!blogMatch.slug) {
      i18n.changeLanguage(code);
      navigate(blogIndexPath(code));
      return;
    }

    let target = blogIndexPath(code);
    try {
      const currentManifestMod = await blogManifestLoaders[blogMatch.locale]();
      const currentManifest = currentManifestMod.default || currentManifestMod;
      const currentArticle = currentManifest.find((a) => a.slug === blogMatch.slug);

      if (currentArticle) {
        const targetManifestMod = await blogManifestLoaders[code]();
        const targetManifest = targetManifestMod.default || targetManifestMod;
        const translated = targetManifest.find((a) => a.translationKey === currentArticle.translationKey);
        if (translated) target = blogArticlePath(code, translated.slug);
      }
    } catch {
      // Manifest failed to load -- fall back to the target locale's index
      // route rather than leaving the switcher stuck mid-click.
    }

    // Drop this navigation if a newer click (or the component's lifecycle)
    // has superseded it -- otherwise a slow, stale manifest fetch could
    // silently redirect the user away from wherever they've navigated to
    // in the meantime (mirrors the `cancelled` guard in App.js's
    // usePrerenderStatusSignal).
    if (mySelection !== selectionRef.current) return;

    i18n.changeLanguage(code);
    navigate(target);
  };

  return (
    <div className="lang-switcher" ref={ref}>
      <button
        className="lang-switcher__trigger"
        onClick={() => setOpen(prev => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {currentLang.label}
        <span className={`lang-switcher__arrow ${open ? 'lang-switcher__arrow--open' : ''}`}>▾</span>
      </button>

      {open && (
        <ul className="lang-switcher__dropdown" role="listbox">
          {others.map(lang => (
            <li key={lang.code}>
              <button
                className="lang-switcher__option"
                onClick={() => handleSelect(lang.code)}
                role="option"
                aria-selected="false"
              >
                {lang.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LangSwitcher;
